import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { getCurrentUser, saveCurrentUser } from "@/lib/utils";
import LanguageService, {
  type LanguageOption,
} from "@/services/api/language";

interface LanguageContextValue {
  languages: LanguageOption[];
  selectedLanguage: LanguageOption | null;
  selectedLanguageCode: string;
  changeLanguage: (languageCode: string) => void;
  isLoading: boolean;
  error: string | null;
  t: (keys: string | string[], fallback?: string) => string;
}

const SELECTED_LANGUAGE_STORAGE_KEY = "selected_language_code";

const LanguageContext = createContext<LanguageContextValue | undefined>(
  undefined,
);

const normalizeToken = (value?: string | null) => value?.trim().toLowerCase() || "";

const buildLookupKeys = (value: string) => {
  const trimmedValue = value.trim();

  if (!trimmedValue) {
    return [];
  }

  const normalizedValue = trimmedValue
    .replace(/[^a-zA-Z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .toLowerCase();
  const dottedValue = normalizedValue.replace(/_/g, ".");
  const kebabValue = normalizedValue.replace(/_/g, "-");

  return Array.from(
    new Set([
      trimmedValue,
      trimmedValue.toLowerCase(),
      normalizedValue,
      dottedValue,
      kebabValue,
    ]),
  );
};

const findLanguageByIdentifier = (
  languages: LanguageOption[],
  identifier?: string | null,
) => {
  const normalizedIdentifier = normalizeToken(identifier);

  if (!normalizedIdentifier) {
    return null;
  }

  return (
    languages.find(
      (language) =>
        normalizeToken(language.languageCode) === normalizedIdentifier ||
        normalizeToken(language.languageName) === normalizedIdentifier,
    ) || null
  );
};

const getStoredLanguage = () => {
  if (typeof window === "undefined") {
    return "";
  }

  return window.localStorage.getItem(SELECTED_LANGUAGE_STORAGE_KEY) || "";
};

const persistSelectedLanguage = (languageCode: string) => {
  if (typeof window !== "undefined") {
    window.localStorage.setItem(SELECTED_LANGUAGE_STORAGE_KEY, languageCode);
  }

  const currentUser = getCurrentUser() || {};

  saveCurrentUser({
    language: languageCode,
    userSettings: {
      ...(currentUser.userSettings || {}),
      language: languageCode,
    },
  });
};

export const LanguageProvider = ({ children }: { children: ReactNode }) => {
  const [languages, setLanguages] = useState<LanguageOption[]>([]);
  const [selectedLanguage, setSelectedLanguage] = useState<LanguageOption | null>(
    null,
  );
  const [selectedLanguageCode, setSelectedLanguageCode] = useState(
    () =>
      getStoredLanguage() ||
      getCurrentUser()?.userSettings?.language ||
      getCurrentUser()?.language ||
      "",
  );
  const [isLoadingLanguages, setIsLoadingLanguages] = useState(false);
  const [isLoadingLanguageDetail, setIsLoadingLanguageDetail] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isLoading = isLoadingLanguages || isLoadingLanguageDetail;

  const applyLanguage = useCallback(async (languageCode: string) => {
    const normalizedLanguageCode = languageCode.trim();

    if (!normalizedLanguageCode) {
      return;
    }

    setSelectedLanguageCode(normalizedLanguageCode);
    setIsLoadingLanguageDetail(true);

    try {
      const response =
        await LanguageService.getLanguageByCode(normalizedLanguageCode);
      const nextLanguage = response.data;

      setSelectedLanguage(nextLanguage);
      setSelectedLanguageCode(nextLanguage.languageCode);
      persistSelectedLanguage(nextLanguage.languageCode);
      setError(null);
    } catch (languageError: any) {
      setError(
        languageError?.response?.data?.message ||
          languageError?.message ||
          "Failed to switch language",
      );
    } finally {
      setIsLoadingLanguageDetail(false);
    }
  }, []);

  useEffect(() => {
    let isMounted = true;

    const loadLanguages = async () => {
      setIsLoadingLanguages(true);

      try {
        const response = await LanguageService.getLanguages();
        const nextLanguages = Array.isArray(response.data) ? response.data : [];

        if (!isMounted) {
          return;
        }

        setLanguages(nextLanguages);
        setError(null);

        const preferredLanguage =
          getStoredLanguage() ||
          getCurrentUser()?.userSettings?.language ||
          getCurrentUser()?.language;
        const fallbackLanguage =
          findLanguageByIdentifier(nextLanguages, preferredLanguage) ||
          findLanguageByIdentifier(
            nextLanguages,
            getCurrentUser()?.userSettings?.language || getCurrentUser()?.language,
          ) ||
          findLanguageByIdentifier(nextLanguages, "english") ||
          nextLanguages[0] ||
          null;

        if (fallbackLanguage) {
          setSelectedLanguageCode(fallbackLanguage.languageCode);
          void applyLanguage(fallbackLanguage.languageCode);
        }
      } catch (languageError: any) {
        if (!isMounted) {
          return;
        }

        setError(
          languageError?.response?.data?.message ||
            languageError?.message ||
            "Failed to load languages",
        );
      } finally {
        if (isMounted) {
          setIsLoadingLanguages(false);
        }
      }
    };

    void loadLanguages();

    return () => {
      isMounted = false;
    };
  }, [applyLanguage]);

  const changeLanguage = useCallback(
    (languageCode: string) => {
      void applyLanguage(languageCode);
    },
    [applyLanguage],
  );

  useEffect(() => {
    if (selectedLanguage?.languageCode) {
      document.documentElement.lang = selectedLanguage.languageCode;
    }
  }, [selectedLanguage?.languageCode]);

  const t = useCallback(
    (keys: string | string[], fallback?: string) => {
      const translationMap =
        selectedLanguage?.translations ||
        findLanguageByIdentifier(languages, selectedLanguageCode)?.translations ||
        {};
      const keyList = Array.isArray(keys) ? keys : [keys];
      const candidateKeys = Array.from(
        new Set(
          [...keyList, fallback || ""].flatMap((key) =>
            key ? buildLookupKeys(key) : [],
          ),
        ),
      );

      for (const key of candidateKeys) {
        const translatedValue = translationMap[key];

        if (typeof translatedValue === "string" && translatedValue.trim()) {
          return translatedValue;
        }
      }

      return fallback || keyList[0] || "";
    },
    [languages, selectedLanguage, selectedLanguageCode],
  );

  const contextValue = useMemo(
    () => ({
      languages,
      selectedLanguage,
      selectedLanguageCode,
      changeLanguage,
      isLoading,
      error,
      t,
    }),
    [
      changeLanguage,
      error,
      isLoading,
      languages,
      selectedLanguage,
      selectedLanguageCode,
      t,
    ],
  );

  return (
    <LanguageContext.Provider value={contextValue}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);

  if (!context) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }

  return context;
};
